import {
  File,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useStorageSignedUrl } from "@/hooks/useStorageSignedUrl";
import { openPrivateStorageFile } from "@/lib/supabase/privateStorageFile";
import { isResolvableStorageReference } from "@/lib/supabase/storageObjectUrl";
import {
  FILE_KIND_BADGE,
  getFileKind,
  type FileKind,
} from "@/lib/fileAttachmentKinds";

export type { FileKind } from "@/lib/fileAttachmentKinds";
export { getFileKind } from "@/lib/fileAttachmentKinds";

export type FileAttachment = {
  title?: string;
  type?: string;
  src?: string;
  path?: string;
  /** MIME Content-ID for inline email images (cid:…). */
  contentId?: string | null;
  /** Stored attachment size when known (bytes). */
  sizeBytes?: number | null;
};

const FILE_KIND_META: Record<
  FileKind,
  { Icon: LucideIcon; iconClassName: string; tileClassName: string }
> = {
  pdf: {
    Icon: FileText,
    iconClassName: "text-red-600",
    tileClassName: "bg-red-50",
  },
  image: {
    Icon: FileImage,
    iconClassName: "text-sky-600",
    tileClassName: "bg-sky-50",
  },
  spreadsheet: {
    Icon: FileSpreadsheet,
    iconClassName: "text-emerald-600",
    tileClassName: "bg-emerald-50",
  },
  document: {
    Icon: FileText,
    iconClassName: "text-blue-600",
    tileClassName: "bg-blue-50",
  },
  presentation: {
    Icon: FileText,
    iconClassName: "text-orange-600",
    tileClassName: "bg-orange-50",
  },
  archive: {
    Icon: FileArchive,
    iconClassName: "text-amber-700",
    tileClassName: "bg-amber-50",
  },
  file: {
    Icon: File,
    iconClassName: "text-muted-foreground",
    tileClassName: "bg-muted/60",
  },
};

export const FileTypeIcon = ({
  file,
  className,
  iconClassName,
}: {
  file: FileAttachment;
  className?: string;
  iconClassName?: string;
}) => {
  const kind = getFileKind(file);
  const { Icon, iconClassName: defaultIconClass } = FILE_KIND_META[kind];
  return (
    <Icon
      className={cn(
        "size-4 shrink-0",
        defaultIconClass,
        iconClassName,
        className,
      )}
    />
  );
};

/** Colored Word / Excel / PDF-style glyph used in mail and attachment lists. */
export const FileTypeBadge = ({
  file,
  className,
  size = "md",
}: {
  file: FileAttachment;
  className?: string;
  size?: "sm" | "md";
}) => {
  const kind = getFileKind(file);
  const { label, className: badgeClass } = FILE_KIND_BADGE[kind];
  const box = size === "sm" ? "size-5 text-3xs" : "size-7 text-2xs";
  return (
    <span
      aria-hidden
      title={file.title || kind}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded font-bold leading-none tracking-tight",
        box,
        badgeClass,
        className,
      )}
    >
      {label}
    </span>
  );
};

export const FileAttachmentPill = ({ file }: { file: FileAttachment }) => {
  const kind = getFileKind(file);
  const reference = file.src?.trim() || file.path?.trim();
  const isPdf = kind === "pdf";
  const href = useStorageSignedUrl(reference, {
    path: file.path,
    defaultBucket: "attachments",
    enabled: !isPdf,
  });
  const isResolving =
    !isPdf &&
    Boolean(reference) &&
    !href &&
    isResolvableStorageReference(reference, "attachments");

  if (!reference) return null;

  return (
    <a
      href={isPdf ? "#" : (href ?? "#")}
      target="_blank"
      rel="noreferrer"
      title={file.title || "Attachment"}
      aria-busy={isResolving}
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-md border bg-background px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted/50",
        isResolving && "pointer-events-none opacity-70",
      )}
      onClick={(event) => {
        event.stopPropagation();
        if (isPdf) {
          event.preventDefault();
          void openPrivateStorageFile({
            reference,
            defaultBucket: "attachments",
            filename: file.title || "file.pdf",
          }).catch(() => {});
          return;
        }
        if (!href) event.preventDefault();
      }}
    >
      <FileTypeBadge file={file} size="sm" />
      <span className="truncate">
        {isResolving ? "Loading attachment…" : file.title || "Attachment"}
      </span>
    </a>
  );
};

export const FileAttachmentPillList = ({
  attachments,
  className,
}: {
  attachments: unknown[];
  className?: string;
}) => {
  const files = attachments
    .map((item) => item as FileAttachment)
    .filter((file) => file.src?.trim() || file.path?.trim());

  if (!files.length) return null;

  return (
    <div className={cn("mt-3 flex flex-wrap gap-2", className)}>
      {files.map((file, index) => (
        <FileAttachmentPill key={`${file.src}-${index}`} file={file} />
      ))}
    </div>
  );
};

export const FileAttachmentIconLink = ({
  file,
  size = "md",
}: {
  file: FileAttachment;
  size?: "sm" | "md";
}) => {
  const kind = getFileKind(file);
  const { tileClassName } = FILE_KIND_META[kind];
  const tileSize = size === "sm" ? "size-7" : "size-9";
  const reference = file.src?.trim() || file.path?.trim();
  const href = useStorageSignedUrl(reference, {
    path: file.path,
    defaultBucket: "attachments",
  });
  const isResolving =
    Boolean(reference) &&
    !href &&
    isResolvableStorageReference(reference, "attachments");

  if (!reference) return null;

  return (
    <a
      href={href ?? "#"}
      target="_blank"
      rel="noreferrer"
      title={file.title || "Attachment"}
      aria-busy={isResolving}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-md border transition-colors hover:opacity-90",
        tileSize,
        tileClassName,
        isResolving && "pointer-events-none opacity-70",
      )}
      onClick={(event) => {
        event.stopPropagation();
        if (!href) event.preventDefault();
      }}
    >
      <FileTypeBadge file={file} size={size === "sm" ? "sm" : "md"} />
    </a>
  );
};

export const FileAttachmentIconRow = ({
  attachments,
  size = "md",
  max = 4,
}: {
  attachments: unknown[];
  size?: "sm" | "md";
  max?: number;
}) => {
  const files = attachments
    .map((item) => item as FileAttachment)
    .filter((file) => file.src?.trim() || file.path?.trim());

  if (!files.length) return null;

  const visible = files.slice(0, max);
  const overflow = files.length - visible.length;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visible.map((file, index) => (
        <FileAttachmentIconLink
          key={`${file.src}-${index}`}
          file={file}
          size={size}
        />
      ))}
      {overflow > 0 ? (
        <span className="text-2xs text-muted-foreground">+{overflow}</span>
      ) : null}
    </div>
  );
};
